import type {
  AnalyticsResponse,
  AnswerInputRaw,
  ApiErrorBody,
  ClientBriefResponse,
  ClientConfig,
  ClientSessionResponse,
  ConfirmInput,
  ConsentInput,
  ContactConsentInput,
  ContactInput,
  CreateSessionInput,
  CreateSessionResponse,
  ConsultantConfigResponse,
  CreateUserInput,
  DeletionRequestView,
  DeliveryResponse,
  EvalScoreInput,
  FinishResponse,
  FollowupAnswerInput,
  Lang,
  LlmCallView,
  MemberCheckLine,
  MeResponse,
  NewLinkResponse,
  OkResponse,
  PublicConfig,
  Questionnaire,
  QuestionnaireVersionItem,
  ReleaseResponse,
  SessionDetailResponse,
  SessionListItem,
  SecondOpinionInput,
  SessionPatchInput,
  Track,
  UserView,
} from '@aera/schema';

/** Same origin in production (nginx proxies /api); Vite proxies /api in development. */
const BASE: string = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The failing paths of a validation error (ApiErrorBody.details), e.g. "pains.0.severity". */
    readonly details: NonNullable<ApiErrorBody['details']> = [],
    /** Seconds to wait before trying again (Retry-After on a 429), when the API said. */
    readonly retryAfter?: number,
    /** The whole error body, for the fields beside error and message (e.g. SessionDeletedBody's deletedAt). */
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...(signal ? { signal } : {}),
    method,
    credentials: 'include',
    headers: body === undefined ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let code = 'http_error';
    let message = res.statusText;
    let details: NonNullable<ApiErrorBody['details']> = [];
    let body: Record<string, unknown> = {};
    try {
      const data = (await res.json()) as Partial<ApiErrorBody> & { code?: string };
      if (data && typeof data === 'object') body = data as Record<string, unknown>;
      code = data.code ?? data.error ?? code;
      message = data.message ?? data.error ?? message;
      if (Array.isArray(data.details)) details = data.details.filter((d) => d && typeof d.path === 'string');
    } catch {
      /* not JSON */
    }
    const retryAfter = Number(res.headers.get('retry-after') ?? NaN);
    throw new ApiError(res.status, code, message, details, retryAfter > 0 ? retryAfter : undefined, body);
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  return (type.includes('application/json') ? await res.json() : undefined) as T;
}

/** List endpoints may answer with a bare array or wrap it ({ sessions: [...] }); accept both. */
function unwrap<T>(value: unknown, key: string): T {
  if (value && typeof value === 'object' && !Array.isArray(value) && key in value) return (value as Record<string, T>)[key]!;
  return value as T;
}

const enc = encodeURIComponent;

/* ------------------------------------------------------------------ *
 * Client routes: scoped by the session token, no login.
 * ------------------------------------------------------------------ */


export const clientApi = {
  session: (token: string) => request<ClientSessionResponse>('GET', `/api/s/${enc(token)}`),
  /** `signal` lets autosave abort a write stuck on a stalled connection before it retries. */
  patch: (token: string, body: SessionPatchInput, signal?: AbortSignal) => request<OkResponse>('PATCH', `/api/s/${enc(token)}`, body, signal),
  consent: (token: string, body: ConsentInput) => request<OkResponse>('POST', `/api/s/${enc(token)}/consent`, body),
  answer: (token: string, questionId: string, body: AnswerInputRaw | ContactInput | ContactConsentInput, signal?: AbortSignal) =>
    request<OkResponse>('PUT', `/api/s/${enc(token)}/answers/${enc(questionId)}`, body, signal),
  complete: (token: string) => request<OkResponse>('POST', `/api/s/${enc(token)}/complete`),
  followup: (token: string, id: string, body: FollowupAnswerInput) =>
    request<OkResponse>('PUT', `/api/s/${enc(token)}/followups/${enc(id)}`, body),
  confirm: (token: string, body: ConfirmInput) => request<{ ok: true; memberCheck: MemberCheckLine[] }>('PUT', `/api/s/${enc(token)}/confirm`, body),
  finish: (token: string) => request<FinishResponse>('POST', `/api/s/${enc(token)}/finish`),
  deleteRequest: (token: string, reason?: string) =>
    request<OkResponse>('POST', `/api/s/${enc(token)}/delete-request`, reason ? { reason } : {}),
  /** `sent: false` (with a reason) when nothing went out, e.g. no mail transport or no I5 consent. */
  sendBrief: (token: string) => request<DeliveryResponse>('POST', `/api/s/${enc(token)}/send-brief`),
  eventsUrl: (token: string) => `${BASE}/api/s/${enc(token)}/events`,
  brief: (briefToken: string) => request<ClientBriefResponse>('GET', `/api/b/${enc(briefToken)}`),
  briefDeleteRequest: (briefToken: string) => request<OkResponse>('POST', `/api/b/${enc(briefToken)}/delete-request`, {}),
  /** The brief link page's own "email me this": same rules and answer as sendBrief. */
  briefSendEmail: (briefToken: string) => request<DeliveryResponse>('POST', `/api/b/${enc(briefToken)}/send-brief`),
  /** The consent placeholders for /privacy, which has no session token to load ClientConfig with. */
  publicConfig: () => request<PublicConfig>('GET', '/api/public-config'),
};

export type { PublicConfig };

/* ------------------------------------------------------------------ *
 * Consultant routes: JWT in an httpOnly cookie.
 * ------------------------------------------------------------------ */

const unauthorisedListeners = new Set<() => void>();
const authorisedListeners = new Set<() => void>();

const subscribe = (listeners: Set<() => void>, listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Called when a consultant call answers 401 (the 12-hour cookie expired, the account signed out everywhere
 * or was disabled), so the dashboard can drop its signed-in state. Returns the unsubscribe.
 */
export const onUnauthorised = (listener: () => void): (() => void) => subscribe(unauthorisedListeners, listener);

/** Called when a consultant call succeeds, e.g. after the consultant signed in again in another tab. */
export const onAuthorised = (listener: () => void): (() => void) => subscribe(authorisedListeners, listener);

/** request() for the consultant routes: a 401 also tells the dashboard it is signed out, a success that it is not. */
function consultant<T>(method: string, path: string, body?: unknown): Promise<T> {
  return request<T>(method, path, body).then(
    (value) => {
      for (const listener of authorisedListeners) listener();
      return value;
    },
    (e: unknown) => {
      if (e instanceof ApiError && e.status === 401) for (const listener of unauthorisedListeners) listener();
      throw e;
    },
  );
}

/** GET /api/print/:id: the session detail, plus PRIVACY_EMAIL for the footer of a client brief printed before release. */
export type PrintResponse = SessionDetailResponse & { privacyEmail?: string };

export interface SessionFilters {
  status?: string;
  /** 'none': the invites whose client has not chosen a track yet. */
  track?: Track | 'none' | '';
  q?: string;
  flagged?: boolean;
}

export const adminApi = {
  login: (email: string, password: string) =>
    request<unknown>('POST', '/api/auth/login', { email, password }).then((r) => unwrap<UserView>(r, 'user')),
  logout: () => consultant<OkResponse>('POST', '/api/auth/logout'),
  /** Resolves with null when nobody is signed in (the API answers 200 { user: null }). */
  me: () => consultant<MeResponse>('GET', '/api/auth/me').then((r) => r.user ?? null),
  config: () => consultant<ConsultantConfigResponse>('GET', '/api/config'),

  sessions: (filters: SessionFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.track) params.set('track', filters.track);
    if (filters.q) params.set('q', filters.q);
    if (filters.flagged) params.set('flagged', '1');
    const qs = params.toString();
    return consultant<unknown>('GET', `/api/sessions${qs ? `?${qs}` : ''}`).then((r) => unwrap<SessionListItem[]>(r, 'sessions'));
  },
  createSession: (body: CreateSessionInput) => consultant<CreateSessionResponse>('POST', '/api/sessions', body),
  session: (id: string) => consultant<SessionDetailResponse>('GET', `/api/sessions/${enc(id)}`),
  regenerate: (id: string) => consultant<OkResponse>('POST', `/api/sessions/${enc(id)}/regenerate`),
  /** A fresh questionnaire link for the same session (30 days); the previous link stops working. */
  newLink: (id: string) => consultant<NewLinkResponse>('POST', `/api/sessions/${enc(id)}/new-link`),
  release: (id: string) => consultant<ReleaseResponse>('POST', `/api/sessions/${enc(id)}/release`),
  translate: (id: string, lang: Lang) => consultant<OkResponse>('POST', `/api/sessions/${enc(id)}/translate`, { lang }),
  secondOpinion: (id: string, body: SecondOpinionInput) => consultant<OkResponse>('POST', `/api/sessions/${enc(id)}/second-opinion`, body),
  llmCalls: (id: string) =>
    consultant<unknown>('GET', `/api/sessions/${enc(id)}/llm-calls`).then((r) => unwrap<LlmCallView[]>(r, 'calls')),
  /** `sent: false` (with a reason) when the card did not go out, e.g. no webhook configured. */
  shareFeishu: (id: string) => consultant<DeliveryResponse>('POST', `/api/sessions/${enc(id)}/share-feishu`),
  exportPdfUrl: (id: string, kind: 'internal' | 'client') => `${BASE}/api/sessions/${enc(id)}/export.pdf?kind=${kind}`,
  /** Where the PDF buttons go when the server has no Chromium (config.pdfExport 'browser'): the print page, printed here. */
  printPageUrl: (id: string, kind: 'internal' | 'client') => `/admin/print/${enc(id)}?kind=${kind}&autoprint=1`,
  patchBrief: (id: string, body: { json?: unknown; markdown?: { lang: Lang; text: string } }) =>
    consultant<unknown>('PATCH', `/api/briefs/${enc(id)}`, body),
  /** Puppeteer's print token, not the login cookie: a 401 here says nothing about the consultant's sign-in. */
  print: (id: string, t: string) => request<PrintResponse>('GET', `/api/print/${enc(id)}?t=${enc(t)}`),

  questionnaires: (track: Track) =>
    consultant<unknown>('GET', `/api/questionnaires?track=${track}`).then((r) => unwrap<QuestionnaireVersionItem[]>(r, 'versions')),
  questionnaire: (track: Track, version: number) =>
    consultant<unknown>('GET', `/api/questionnaires/${version}?track=${track}`).then((r) => unwrap<Questionnaire>(r, 'questionnaire')),
  /** Returns the open draft, or starts one from the latest published version. */
  draft: (track: Track) =>
    consultant<unknown>('POST', `/api/questionnaires/draft?track=${track}`).then((r) => unwrap<Questionnaire>(r, 'questionnaire')),
  saveDraft: (questionnaire: Questionnaire) =>
    consultant<unknown>('PUT', `/api/questionnaires/draft?track=${questionnaire.track}`, { questionnaire }).then((r) =>
      unwrap<Questionnaire>(r, 'questionnaire'),
    ),
  discardDraft: (track: Track) => consultant<OkResponse>('DELETE', `/api/questionnaires/draft?track=${track}`),
  publish: (track: Track) =>
    consultant<unknown>('POST', `/api/questionnaires/publish?track=${track}`).then((r) => unwrap<Questionnaire>(r, 'questionnaire')),

  analytics: (track: Track, version?: number) =>
    consultant<AnalyticsResponse>('GET', `/api/analytics?track=${track}${version ? `&version=${version}` : ''}`),

  deletionRequests: () =>
    consultant<unknown>('GET', '/api/deletion-requests').then((r) => unwrap<DeletionRequestView[]>(r, 'requests')),
  processDeletion: (id: string) => consultant<OkResponse>('POST', `/api/deletion-requests/${enc(id)}/process`),

  users: () => consultant<unknown>('GET', '/api/users').then((r) => unwrap<UserView[]>(r, 'users')),
  createUser: (body: CreateUserInput) => consultant<unknown>('POST', '/api/users', body),

  evals: () => consultant<unknown>('GET', '/api/evals').then((r) => unwrap<EvalRunListItem[]>(r, 'runs')),
  evalRun: (id: string) => consultant<EvalRunDetail>('GET', `/api/evals/${enc(id)}`),
  scoreEval: (id: string, body: EvalScoreInput) => consultant<OkResponse>('POST', `/api/evals/${enc(id)}/scores`, body),
};

/** GET /api/evals: one row per run of `pnpm eval` (a bake-off runs several models in one run). */
export interface EvalRunListItem {
  id: string;
  /** Comma-separated when the run was a bake-off. */
  model: string;
  promptVersions: Record<string, string>;
  scores: number;
  createdAt: string;
}

export interface EvalResultView {
  sessionKey: string;
  title: string;
  model: string;
  ok: boolean;
  issues: string[];
  latencyMs: number;
  internal: unknown;
  clientMarkdown: string | null;
}

export interface EvalScoreView {
  sessionKey: string;
  model?: string;
  byName: string;
  at: string;
  scores: Record<string, number>;
  note: string | null;
}

export interface EvalRunDetail {
  id: string;
  model: string;
  promptVersions: Record<string, string>;
  results: EvalResultView[];
  scores: EvalScoreView[];
  createdAt: string;
}
